import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService, projectService } from '../services/api';
import './Users.css';

const Users = ({ user, onBack }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    internal_number: '',
    collaboration_start_date: '',
    phone: '',
    role: 'employee',
    project_id: '',
    hourly_rate_weekday: 29,
    hourly_rate_weekend: 29,
    hourly_rate_holiday: 29,
    accommodation: 'self'
  });
  const [projects, setProjects] = useState([]);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    userId: null,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState('👤');

  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);

  const avatarOptions = [
    '👨‍💼', '👩‍💼', '👷‍♂️', '👷‍♀️', '👨‍🔧',
    '👩‍🔧', '🧑‍💻', '👨‍🏭', '👩‍🏭', '🧑‍🔬'
  ];

  const loadUsers = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Non-admin users only see their own profile
      if (user.role === 'admin' || user.role === 'supervisor') {
        const { items, total: t } = await userService.getAllPaged({ page, page_size: pageSize, q: search });
        setUsers(items);
        setTotal(t);
      } else {
        // Regular users only see their own profile
        const userData = await userService.getById(user.id);
        setUsers([userData]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Chyba pri načítavaní používateľov: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, user]);

  const loadProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  }, []);

  // Initial load of projects when user available
  useEffect(() => {
    if (!user) return;
    loadProjects();
  }, [loadProjects, user]);

  // Load/reload users when pagination/search/user changes
  useEffect(() => {
    if (!user) return;
    loadUsers();
  }, [loadUsers, user]);

  // Safety check for user
  if (!user) {
    return (
      <div className="users-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam používateľské údaje...</p>
        </div>
      </div>
    );
  }

  const handleBlockUser = async (userId, isBlocked) => {
    try {
      const action = isBlocked ? 'odblokovať' : 'zablokovať';
      const confirmMessage = `Naozaj chcete ${action} tohto používateľa?`;

      if (window.confirm(confirmMessage)) {
        try {
          // Toggle the blocked status
          const newBlockedStatus = !isBlocked;
          await userService.block(userId, newBlockedStatus);
          loadUsers(); // Reload users to reflect changes
          alert(`Používateľ bol úspešne ${isBlocked ? 'odblokovaný' : 'zablokovaný'}!`);
        } catch (error) {
          throw new Error('Chyba pri aktualizácii stavu používateľa');
        }
      }
    } catch (error) {
      console.error('Error blocking/unblocking user:', error);
      alert('Chyba pri aktualizácii stavu používateľa: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const baseBody = editingUser
      ? { ...formData, password: undefined }
      : formData;

    const saveUser = async (extra = {}) => {
      const body = { ...baseBody, ...extra };
      if (editingUser) {
        await userService.update(editingUser.id, body);
      } else {
        await userService.create(body);
      }
    };

    const finishSave = () => {
      loadUsers();
      setShowAddForm(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        internal_number: '',
        collaboration_start_date: '',
        phone: '',
        role: 'employee',
        project_id: '',
        hourly_rate_weekday: 29,
        hourly_rate_weekend: 29,
        hourly_rate_holiday: 29,
        accommodation: 'self'
      });
      alert(editingUser ? 'Používateľ bol úspešne upravený!' : 'Používateľ bol úspešne pridaný!');
    };

    const handleInternalNumberConflict = async (error) => {
      const conflict = error.response?.data;
      if (error.response?.status !== 409 || conflict?.error !== 'INTERNAL_NUMBER_CONFLICT') return false;

      const conflictName = conflict.conflict_user?.full_name || conflict.conflict_user?.username || 'iný používateľ';
      const blockExisting = window.confirm(
        `${conflict.message}\n\n` +
        `Ak pôvodný používateľ skončil, klikni OK. Systém zablokuje používateľa ${conflictName} a číslo ${conflict.internal_number} priradí tomuto používateľovi.\n\n` +
        `Ak chceš radšej použiť ďalšie voľné číslo, klikni Zrušiť.`
      );

      try {
        if (blockExisting) {
          await saveUser({
            internal_number_conflict_action: 'block_existing',
            internal_number_conflict_user_id: conflict.conflict_user?.id
          });
          finishSave();
          return true;
        }

        if (conflict.suggested_internal_number && window.confirm(`Použiť najbližšie voľné číslo ${conflict.suggested_internal_number}?`)) {
          await saveUser({
            internal_number: conflict.suggested_internal_number,
            internal_number_conflict_action: 'use_next_available'
          });
          finishSave();
          return true;
        }
      } catch (retryError) {
        console.error('Error saving user after conflict action:', retryError);
        alert('Nastala chyba pri riešení konfliktu interného čísla: ' + (retryError.response?.data?.message || retryError.response?.data?.error || retryError.message));
        return true;
      }

      return true;
    };

    try {
      await saveUser();
      finishSave();
    } catch (error) {
      console.error('Error saving user:', error);
      if (await handleInternalNumberConflict(error)) return;

      const details = error.response?.data?.details;
      const errorMessage = Array.isArray(details) && details.length
        ? details.map(d => d.msg || d.param).join(', ')
        : (error.response?.data?.error || error.message || 'Neznáma chyba');
      alert('Nastala chyba pri ukladaní používateľa: ' + errorMessage);
    }
  };

  const handleEdit = (userToEdit) => {
    navigate(`/users/${userToEdit.id}`);
  };

  const handleDelete = async (userId) => {
    try {
      setDeleteLoading(true);
      const impact = await userService.getDeleteImpact(userId);
      setDeleteImpact(impact);
    } catch (error) {
      console.error('Error loading delete impact:', error);
      const data = error.response?.data;
      alert(data?.error || 'Nastala chyba pri načítaní dopadu mazania používateľa');
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDeleteImpactModal = () => {
    if (deleteLoading) return;
    setDeleteImpact(null);
  };

  const handleConfirmForceDelete = async () => {
    if (!deleteImpact?.user?.id) return;

    try {
      setDeleteLoading(true);
      const result = await userService.delete(deleteImpact.user.id, { force: true });
      setDeleteImpact(null);
      loadUsers(); // Reload users
      const totals = result.impact?.totals;
      const deletedCount = totals ? totals.recordsToDelete : 0;
      const detachedCount = totals ? totals.recordsToDetach : 0;
      const filesCount = totals ? totals.filesToDelete : 0;
      alert(`Používateľ bol úspešne vymazaný.\n\nZmazané záznamy: ${deletedCount}\nOdpojené záznamy: ${detachedCount}\nZmazané súbory: ${filesCount}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      const data = error.response?.data;
      const references = Array.isArray(data?.references) && data.references.length
        ? '\n\nSúvisiace záznamy:\n' + data.references.map(ref => `- ${ref.label}: ${ref.count}`).join('\n')
        : '';
      alert((data?.error || 'Nastala chyba pri mazaní používateľa') + references);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openPasswordForm = (userId) => {
    setPasswordFormData({
      userId,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordForm(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      alert('Nové heslá sa nezhodujú!');
      return;
    }

    if (passwordFormData.newPassword.length < 6) {
      alert('Nové heslo musí mať aspoň 6 znakov!');
      return;
    }

    try {
      const passwordData = {
        new_password: passwordFormData.newPassword
      };

      // Admin nemusí zadávať súčasné heslo
      if (user.role !== 'admin') {
        if (!passwordFormData.currentPassword) {
          alert('Zadajte súčasné heslo!');
          return;
        }
        passwordData.current_password = passwordFormData.currentPassword;
      } else if (passwordFormData.currentPassword) {
        // Ak admin zadal súčasné heslo, pošleme ho
        passwordData.current_password = passwordFormData.currentPassword;
      }

      await userService.changePassword(passwordFormData.userId, passwordData);
      alert('Heslo bolo úspešne zmenené!');
      setShowPasswordForm(false);
      setPasswordFormData({
        userId: null,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Nastala chyba pri zmene hesla: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="users-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam používateľov...</p>
        </div>
      </div>
    );
  }

  const isAdmin = user.role === 'admin' || user.role === 'supervisor';
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;

  const renderPagination = (position = 'top') => {
    if (!isAdmin || total <= pageSize) return null;

    return (
      <div className={`pagination pagination-${position}`}>
        <div className="pagination-summary">
          Spolu: {total} používateľov · Strana {page} z {totalPages}
        </div>
        <div className="pagination-actions">
          <button disabled={!hasPreviousPage} onClick={() => setPage(p => Math.max(p - 1, 1))}>
            ← Predošlá strana
          </button>
          <button disabled={!hasNextPage} onClick={() => setPage(p => Math.min(p + 1, totalPages))}>
            Ďalšia strana →
          </button>
        </div>
      </div>
    );
  };

  const deleteImpactReferences = deleteImpact?.references || [];
  const deleteImpactDeletes = deleteImpactReferences.filter(ref => ref.action === 'delete');
  const deleteImpactDetaches = deleteImpactReferences.filter(ref => ref.action === 'detach');
  const deleteImpactFiles = deleteImpact?.files || [];

  return (
    <div className="users-container">
      <div className="users-header">
        <h1 className="users-title">{isAdmin ? '👥 Správa používateľov' : '👤 Môj profil'}</h1>
        {isAdmin && (
          <button
            className="add-user-btn"
            onClick={() => setShowAddForm(true)}
          >
            ➕ Pridať používateľa
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="users-toolbar" style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'10px' }}>
          <input
            type="text"
            value={search}
            onChange={(e)=>{ setSearch(e.target.value); setPage(1); }}
            placeholder="🔎 Hľadať používateľov..."
            style={{ flex:'1 1 auto', padding:'8px' }}
          />
          <select value={pageSize} onChange={(e)=>{ setPageSize(parseInt(e.target.value)||12); setPage(1); }}>
            <option value={6}>6</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
          </select>
        </div>
      )}


      {renderPagination('top')}

      <div className="users-grid">
        {users.map((userItem, index) => (
          <div key={`user-${userItem.id}`} className={`user-card ${userItem.blocked ? 'blocked' : ''}`}>
            <div className="user-header">
              <div
                className="user-avatar"
                onClick={() => navigate(`/users/${userItem.id}`)}
                style={{ cursor: 'pointer' }}
                title="Kliknite pre zobrazenie profilu"
              >
                {userItem.blocked ? '🚫' : (
                  userItem.avatar && (userItem.avatar.startsWith('user_') || userItem.avatar.includes('.')) ? (
                    <>
                      <img
                        src={`/api/users/${userItem.id}/avatar`}
                        alt={userItem.full_name}
                        className="user-avatar-img"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <span className="user-avatar-fallback" style={{ display: 'none' }}>👤</span>
                    </>
                  ) : (
                    userItem.avatar || '👤'
                  )
                )}
              </div>
              <div
                className="user-main-info"
                onClick={() => navigate(`/users/${userItem.id}`)}
                style={{ cursor: 'pointer' }}
                title="Kliknite pre zobrazenie profilu"
              >
                <div className="user-name-line">
                  <h3 className="user-name">
                    {userItem.full_name || userItem.username}
                  </h3>
                </div>
                <div className="user-contact">
                  <div className="user-username">@{userItem.username}</div>
                  <div className="user-email">{userItem.email}</div>
                  {userItem.internal_number && <div className="user-phone">🔢 Interné číslo: {userItem.internal_number}</div>}
                  {userItem.collaboration_start_date && <div className="user-phone">🤝 Začiatok spolupráce: {new Date(userItem.collaboration_start_date).toLocaleDateString('sk-SK')}</div>}
                  {userItem.phone && <div className="user-phone">📞 {userItem.phone}</div>}
                </div>
              </div>
              {isAdmin && (
                <div className="role-badge-corner">
                  <span className={`role-badge ${userItem.role} ${(userItem.role === 'supervisor' || userItem.role === 'manager') ? 'supervisor-manager' : ''}`}>
                    {userItem.role === 'admin' ? 'Administrátor' :
                     userItem.role === 'supervisor' ? 'Supervízor' :
                     userItem.role === 'manager' ? 'Manažér' : 'Zamestnanec'}
                  </span>
                </div>
              )}
            </div>
            {userItem.project_name && isAdmin && (
              <div className="user-project">
                <span className="project-label">📋 Projekt:</span>
                <span className="project-name">{userItem.project_name}</span>
              </div>
            )}

            {isAdmin && (
              <div className="user-billing-info">
                <div className="billing-rates">
                  {/* 💰 Fakturačné sadzby sú v profile skryté; zobrazujú sa len v dialógu úprav */}
                  <span className="accommodation-label">🏠 Ubytovanie:</span>
                  <span className={`accommodation-status ${userItem.accommodation || 'self'}`}>
                    {(userItem.accommodation === 'company') ? 'Firma platí' : 'Sám platí'}
                  </span>
                </div>
              </div>
            )}
            <div className="user-actions">
              <button className="edit-btn" onClick={() => handleEdit(userItem)}>✏️ Upraviť</button>
              <button className="password-btn" onClick={() => openPasswordForm(userItem.id)}>🔑 Zmeniť heslo</button>
              {isAdmin && (
                <>
                  <button
                    className={`block-btn ${userItem.blocked ? 'unblock' : 'block'}`}
                    onClick={() => handleBlockUser(userItem.id, userItem.blocked)}
                  >
                    {userItem.blocked ? '🔓 Odblokovať' : '🔒 Zablokovať'}
                  </button>
                  <button className="delete-btn" onClick={() => handleDelete(userItem.id)}>🗑️ Vymazať</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {renderPagination('bottom')}


      {deleteImpact && (
        <div className="modal-overlay">
          <div className="modal delete-impact-modal">
            <h2>🗑️ Trvalé vymazanie používateľa</h2>
            <div className="delete-impact-user">
              <strong>{deleteImpact.user?.full_name || deleteImpact.user?.username}</strong>
              <span>@{deleteImpact.user?.username}</span>
              <span>{deleteImpact.user?.email}</span>
            </div>

            <div className="delete-impact-warning">
              Táto akcia je nevratná. Po potvrdení sa používateľ natrvalo vymaže aj s uvedenými väzbami.
            </div>

            <div className="delete-impact-summary">
              <div><strong>{deleteImpact.totals?.recordsToDelete || 0}</strong><span>záznamov sa zmaže</span></div>
              <div><strong>{deleteImpact.totals?.recordsToDetach || 0}</strong><span>záznamov sa odpojí</span></div>
              <div><strong>{deleteImpact.totals?.filesToDelete || 0}</strong><span>súborov sa zmaže</span></div>
            </div>

            <div className="delete-impact-section danger">
              <h3>Zmažú sa tieto dáta</h3>
              {deleteImpactDeletes.length > 0 ? (
                <ul>
                  {deleteImpactDeletes.map(ref => (
                    <li key={`${ref.table}-${ref.column}`}>
                      <strong>{ref.label}:</strong> {ref.count}
                      <small>{ref.impact}</small>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Žiadne historické záznamy na zmazanie.</p>
              )}
            </div>

            {deleteImpactFiles.length > 0 && (
              <div className="delete-impact-section danger">
                <h3>Zmažú sa súbory</h3>
                <ul>
                  {deleteImpactFiles.map((file, index) => (
                    <li key={`${file.type}-${index}`}>
                      <strong>{file.label}</strong>
                      {file.path && <small>{file.path}</small>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {deleteImpactDetaches.length > 0 && (
              <div className="delete-impact-section keep">
                <h3>Tieto dáta ostanú, iba sa odpojí používateľ</h3>
                <ul>
                  {deleteImpactDetaches.map(ref => (
                    <li key={`${ref.table}-${ref.column}`}>
                      <strong>{ref.label}:</strong> {ref.count}
                      <small>{ref.impact}</small>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="delete-impact-actions">
              <button type="button" className="cancel-btn" onClick={closeDeleteImpactModal} disabled={deleteLoading}>
                Zrušiť
              </button>
              <button type="button" className="force-delete-btn" onClick={handleConfirmForceDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Mažem...' : 'Natrvalo vymazať používateľa'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>➕ Pridať nového používateľa</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Používateľské meno:</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {!editingUser && (
                <div className="form-group">
                  <label>Heslo:</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Celé meno:</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Interné číslo pre import:</label>
                <input
                  type="text"
                  name="internal_number"
                  value={formData.internal_number}
                  onChange={handleInputChange}
                  placeholder="napr. 254"
                />
              </div>

              <div className="form-group">
                <label>Začiatok spolupráce:</label>
                <input
                  type="date"
                  name="collaboration_start_date"
                  value={formData.collaboration_start_date || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Telefónne číslo:</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+421 XXX XXX XXX"
                />
              </div>

              {isAdmin && (
                <>
                  <div className="form-group">
                    <label>Rola:</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="employee">Zamestnanec</option>
                      <option value="supervisor">Supervízor</option>
                      <option value="manager">Manažér</option>
                      <option value="admin">Administrátor</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Projekt:</label>
                    <select
                      name="project_id"
                      value={formData.project_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Žiadny projekt</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>


                  <div className="form-section">
                    <h3>💰 Fakturačné sadzby (€/hod)</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Týždeň:</label>
                        <input
                          type="number"
                          name="hourly_rate_weekday"
                          value={formData.hourly_rate_weekday}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Víkend:</label>
                        <input
                          type="number"
                          name="hourly_rate_weekend"
                          value={formData.hourly_rate_weekend}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Sviatky:</label>
                        <input
                          type="number"
                          name="hourly_rate_holiday"
                          value={formData.hourly_rate_holiday}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>🏠 Ubytovanie:</label>
                    <select
                      name="accommodation"
                      value={formData.accommodation}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="self">Sám platí</option>
                      <option value="company">Firma platí</option>
                    </select>
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="save-btn">
                  {editingUser ? '💾 Uložiť zmeny' : '➕ Pridať používateľa'}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingUser(null);
                    setFormData({
                      username: '',
                      email: '',
                      password: '',
                      full_name: '',
                      internal_number: '',
                      phone: '',
                      role: 'employee',
                      project_id: '',
                      hourly_rate_weekday: 29,
                      hourly_rate_weekend: 29,
                      hourly_rate_holiday: 29,
                      accommodation: 'self'
                    });
                  }}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>🔑 Zmena hesla</h2>
            {user.role === 'admin' && passwordFormData.userId !== user.id && (
              <p style={{
                padding: '10px',
                backgroundColor: '#e3f2fd',
                borderLeft: '4px solid #2196f3',
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                ℹ️ Ako admin meníte heslo iného používateľa. Súčasné heslo nie je potrebné.
              </p>
            )}
            <form onSubmit={handlePasswordSubmit}>
              {(user.role !== 'admin' || passwordFormData.userId === user.id) && (
                <div className="form-group">
                  <label>Súčasné heslo:</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordFormData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Nové heslo:</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordFormData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label>Potvrdiť nové heslo:</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordFormData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  🔑 Zmeniť heslo
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordFormData({
                      userId: null,
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="modal-overlay" onClick={() => setShowAvatarPicker(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">🎭 Vyberte avatar</h2>
            <div className="avatar-grid">
              {avatarOptions.map((avatar, index) => (
                <div
                  key={index}
                  className={`avatar-option ${selectedAvatar === avatar ? 'selected' : ''}`}
                  onClick={() => setSelectedAvatar(avatar)}
                >
                  {avatar}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button
                className="submit-btn"
                onClick={async () => {
                  try {
                    await userService.update(user.id, { avatar: selectedAvatar });
                    alert('✅ Avatar bol úspešne zmenený!');
                    setShowAvatarPicker(false);
                    loadUsers();
                  } catch (error) {
                    console.error('Error updating avatar:', error);
                    alert('❌ Chyba pri zmene avatara: ' + (error.response?.data?.error || error.message));
                  }
                }}
              >
                ✅ Potvrdiť
              </button>
              <button
                className="cancel-btn"
                onClick={() => setShowAvatarPicker(false)}
              >
                ❌ Zrušiť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
